import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { ProtectedRoute, AdminRoute, ProfileGuard } from '../components';

// Import pages
import HomePage from '../app/home/page';
import AboutPage from '../app/about/page';
import HowItWorksPage from '../app/how-it-works/page';
import CreatorsPage from '../app/creators/page';
import FansPage from '../app/fans/page';
// /beta landing page shelved along with the signup/beta endpoint
// (audit L4 / #486). Component kept at client/src/app/beta-landing/
// for reuse when we bring beta signup back with a real design.
// import Landing from '../app/beta-landing/page';
import Login from '../app/login/page';
import Signup from '../app/signup/page';
import ForgotPassword from '../app/forgot-password/page';
import ResetPassword from '../app/reset-password/page';
import VerifyEmail from '../app/verify-email/page';
import AgentVerifyEmail from '../app/agent-verify-email/page';
import CreateProfile from '../app/create-profile/page';
import DashboardPage from '../app/dashboard/page';
import SettingsPage from '../app/settings/page';
import AnalyticsPage from '../app/analytics/page';
import MarketplacePage from '@/app/marketplace/page';
import LeaderboardsPage from '@/app/leaderboards/page';
import PostDetailPage from '../app/post/[postId]/page';
import DiscoveryPage from '@/app/discovery/page';
import ProfilePage from '../app/profile/[username]/page';
import MessagesPage from '../app/messages/page';
import FeedPage from '../app/feed/page';
import HashtagFeedPage from '../app/feed/hashtag/page';
import CreatePostPage from '../app/create-post/page';
import ChatPage from '../app/chat/page';
import AchievementsPage from '../app/achievements/page';
import RegisterAgentPage from '../app/agents/register/page';
import MyAgentsPage from '../app/agents/mine/page';
import EditAgentPage from '../app/agents/mine/edit/page';
import AgentDiscoverPage from '../app/agents/discover/page';
import AgentsPage from '../app/agents/page';

// Admin pages
import AdminLayout from '../components/Admin/AdminLayout';
import AdminDashboardPage from '../app/admin/dashboard/page';
import AdminSyncPage from '../app/admin/sync/page';
import SystemNotificationsPage from '../app/admin/system-notifications/page';
import AdminSpotlightPricingPage from '../app/dashboard-admin/spotlight-pricing/page';
import AdminSpotlightAnalyticsPage from '../app/dashboard-admin/spotlight-analytics/page';
import AdminSpotlightTransactionsPage from '../app/dashboard-admin/spotlight-transactions/page';
import AdminAccessTransactionsPage from '../app/dashboard-admin/access-transactions/page';
import AdminNetworkFeesPage from '../app/dashboard-admin/network-fees/page';
import AdminLocationsPage from '../app/dashboard-admin/locations/page';
import AdminChatRoomsPage from '../app/dashboard-admin/chat-rooms/page';
import AdminUserGrowthPage from '../app/admin/user-growth/page';
import AdminAccessAuditPage from '../app/admin/access-audit/page';
import AdminAchievementEarnPage from '../app/admin/achievement-earn/page';

/**
 * Application routing configuration
 *
 * Public routes (viewable without auth per ADR-007):
 * - / - Landing / home
 * - /about - Marketing
 * - /beta - Beta landing
 * - /login, /signup, /forgot-password, /verify-email - Auth flows
 * - /profile/:username - Creator profile page (the wedge)
 * - /post/:postId - Post detail page
 * - /achievements/:username - Creator achievements = reputation signal
 * - /leaderboards, /leaderboards/:board - Creator rankings
 *
 * Protected routes (require authentication):
 * - /dashboard - User dashboard
 * - /feed, /feed/hashtag/:tag - Personalised feed
 * - /discovery - Discover profiles near you
 * - /marketplace - NFT profile marketplace
 * - /messages - Direct messaging
 * - /settings, /analytics, /achievements (no username)
 * - /chat, /create-post, /create-profile
 *
 * Admin routes (require authentication + admin role):
 * - /admin/* - Admin dashboards + sync + system notifications
 * - /dashboard-admin/* - Legacy admin surfaces
 */
export const AppRoutes: React.FC = () => {
  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<HomePage />} />
      <Route path="/about" element={<AboutPage />} />
      <Route path="/how-it-works" element={<HowItWorksPage />} />
      <Route path="/creators" element={<CreatorsPage />} />
      <Route path="/fans" element={<FansPage />} />
      {/* /beta route shelved — see import comment above. */}
      <Route path="/login" element={<Login />} />
      <Route path="/signup" element={<Signup />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/verify-email" element={<VerifyEmail />} />
      {/* ADR-012 agent email verification — public, opens from a
          Resend-delivered link with ?token=. Distinct from
          /verify-email (human account activation). */}
      <Route path="/agent-verify-email" element={<AgentVerifyEmail />} />

      {/* Protected Routes */}
      <Route
        path="/create-profile"
        element={
          <ProtectedRoute>
            <CreateProfile />
          </ProtectedRoute>
        }
      />
      {/* ADR-011 Phase 2b-2a — agent registration form (managed custody,
          subsidy path). ProfileGuard ensures the owner has their own
          profile before registering agents for it. */}
      <Route
        path="/agents/register"
        element={
          <ProtectedRoute>
            <ProfileGuard>
              <RegisterAgentPage />
            </ProfileGuard>
          </ProtectedRoute>
        }
      />
      {/* ADR-011 Ring 1 — "My agents" listing. Owner sees the agents
          they've registered + click through to each. */}
      <Route
        path="/agents/mine"
        element={
          <ProtectedRoute>
            <ProfileGuard>
              <MyAgentsPage />
            </ProfileGuard>
          </ProtectedRoute>
        }
      />
      {/* ADR-011 Ring 2.5 — owner-scoped edit page for a single
          agent. Update profile fields + add/remove endpoints. */}
      <Route
        path="/agents/mine/:agentUserId/edit"
        element={
          <ProtectedRoute>
            <ProfileGuard>
              <EditAgentPage />
            </ProfileGuard>
          </ProtectedRoute>
        }
      />
      {/* Public /agents marketing page — mirrors /creators + /fans.
          "Own the wallet, own the profile, own the reputation — for
          humans AND agents." One click deeper than the home page's
          "AI agents" tile / audience-split card; two CTAs on this
          page fan out to /agents/register (build) + /agents/discover
          (browse). */}
      <Route path="/agents" element={<AgentsPage />} />
      {/* Public agent discover surface — every agent, filterable
          by capability, cursor-paginated. Anonymous-viewable so
          third parties + prospective builders can browse without
          signup. Backing API /api/agents/discover is public too. */}
      <Route path="/agents/discover" element={<AgentDiscoverPage />} />
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <ProfileGuard>
              <DashboardPage />
            </ProfileGuard>
          </ProtectedRoute>
        }
      />
      <Route
        path="/feed"
        element={
          <ProtectedRoute>
            <ProfileGuard>
              <FeedPage />
            </ProfileGuard>
          </ProtectedRoute>
        }
      />
      <Route
        path="/feed/hashtag/:tag"
        element={
          <ProtectedRoute>
            <ProfileGuard>
              <HashtagFeedPage />
            </ProfileGuard>
          </ProtectedRoute>
        }
      />
      <Route
        path="/discovery"
        element={
          <ProtectedRoute>
            <ProfileGuard>
              <DiscoveryPage />
            </ProfileGuard>
          </ProtectedRoute>
        }
      />
      <Route
        path="/messages"
        element={
          <ProtectedRoute>
            <ProfileGuard>
              <MessagesPage />
            </ProfileGuard>
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings"
        element={
          <ProtectedRoute>
            <ProfileGuard>
              <SettingsPage />
            </ProfileGuard>
          </ProtectedRoute>
        }
      />
      {/* <Route
        path="/token-purchase"
        element={
          <ProtectedRoute>
            <ProfileGuard>
              <TokenPurchasePage />
            </ProfileGuard>
          </ProtectedRoute>
        }
      /> */}
      <Route
        path="/analytics"
        element={
          <ProtectedRoute>
            <ProfileGuard>
              <AnalyticsPage />
            </ProfileGuard>
          </ProtectedRoute>
        }
      />
      {/* Public per ADR-007 — the creator profile page IS the wedge.
          No guard: anonymous visitors must see profiles; a logged-in
          user who hasn't finished setup must ALSO be able to browse
          others' profiles (otherwise direct-link / refresh of
          /profile/@someone-else boots them to /create-profile).
          Any logged-in actions on the page (follow, buy, subscribe)
          are guarded at the action layer, not the route. The old
          ProfileGuard wrapper here conflated "viewing" with "acting"
          and broke direct-link + refresh for mid-setup users. */}
      <Route path="/profile/:username" element={<ProfilePage />} />
      <Route
        path="/achievements"
        element={
          <ProtectedRoute>
            <ProfileGuard>
              <AchievementsPage />
            </ProfileGuard>
          </ProtectedRoute>
        }
      />
      {/* Public per ADR-007 — creator's public achievements = reputation signal.
          The /achievements (no username) route above stays behind ProtectedRoute
          since it implicitly means "my achievements" — meaningless for anon. */}
      <Route
        path="/achievements/:username"
        element={
          <ProfileGuard>
            <AchievementsPage />
          </ProfileGuard>
        }
      />
      <Route
        path="/marketplace"
        element={
          <ProtectedRoute>
            <ProfileGuard>
              <MarketplacePage />
            </ProfileGuard>
          </ProtectedRoute>
        }
      />
      {/* Public per ADR-007 — leaderboards are creator rankings, discoverable
          without an account. Server-side has always used optionalAuth here. */}
      <Route
        path="/leaderboards"
        element={
          <ProfileGuard>
            <LeaderboardsPage />
          </ProfileGuard>
        }
      />
      <Route
        path="/leaderboards/:board"
        element={
          <ProfileGuard>
            <LeaderboardsPage />
          </ProfileGuard>
        }
      />
       {/* <Route
        path="/pools"
        element={
          <ProtectedRoute>
            <ProfileGuard>
              <PoolsPage />
            </ProfileGuard>
          </ProtectedRoute>
        }
      /> */}
      <Route
        path="/chat"
        element={
          <ProtectedRoute>
            <ProfileGuard>
              <ChatPage />
            </ProfileGuard>
          </ProtectedRoute>
        }
      />
      <Route
        path="/create-post"
        element={
          <ProtectedRoute>
            <ProfileGuard>
              <CreatePostPage />
            </ProfileGuard>
          </ProtectedRoute>
        }
      />
      {/* Public per ADR-007 — post detail page (second-most important after profile). */}
      <Route
        path="/post/:postId"
        element={
          <ProfileGuard>
            <PostDetailPage />
          </ProfileGuard>
        }
      />

      {/* Admin Routes - require admin role */}
      <Route
        path="/admin"
        element={
          <AdminRoute>
            <AdminLayout />
          </AdminRoute>
        }
      >
        <Route path="dashboard" element={<AdminDashboardPage />} />
        <Route path="sync" element={<AdminSyncPage />} />
        <Route path="system-notifications" element={<SystemNotificationsPage />} />
        <Route path="user-growth" element={<AdminUserGrowthPage />} />
        <Route path="access-audit" element={<AdminAccessAuditPage />} />
        <Route path="achievement-earn" element={<AdminAchievementEarnPage />} />
      </Route>

      {/* Legacy admin routes (wrapped with AdminLayout for consistency) */}
      <Route
        path="/dashboard-admin"
        element={
          <AdminRoute>
            <AdminLayout />
          </AdminRoute>
        }
      >
        <Route path="spotlight-pricing" element={<AdminSpotlightPricingPage />} />
        <Route path="spotlight-analytics" element={<AdminSpotlightAnalyticsPage />} />
        <Route path="spotlight-transactions" element={<AdminSpotlightTransactionsPage />} />
        <Route path="access-transactions" element={<AdminAccessTransactionsPage />} />
        <Route path="network-fees" element={<AdminNetworkFeesPage />} />
        <Route path="locations" element={<AdminLocationsPage />} />
        <Route path="chat-rooms" element={<AdminChatRoomsPage />} />
      </Route>

      {/* Catch-all — send unknown paths to the home page, not /login.
          Login-redirect for unknown paths is only correct in a walled
          garden; post-ADR-007 that assumption is gone. Anon visitors
          who mistype a URL should land on the public homepage, from
          which they can navigate or sign up. */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

export default AppRoutes;
